import md5 from 'md5';

// Per-tab recovery survives a reload. Store only opaque keys and session IDs,
// never the swipe, OAuth code, QR token, or request body. The digest is a local
// lookup index, not a security boundary; the server checks its own SHA-256 hash.
const memory = new Map();
const PREFIX = 'pinpoint_operation:';

function canonical(value) {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.keys(value).sort().map(key => [key, canonical(value[key])]));
  }
  return value;
}

export function operationSlot(scope, intent) {
  return PREFIX + md5(JSON.stringify(canonical([scope, intent])));
}

export function readOperation(slot) {
  try {
    const stored = window.sessionStorage.getItem(slot);
    if (stored) return JSON.parse(stored);
  } catch (_) { /* Memory recovery still works when browser storage is disabled. */ }
  return memory.get(slot) || null;
}

export function saveOperation(slot, value) {
  memory.set(slot, value);
  try { window.sessionStorage.setItem(slot, JSON.stringify(value)); } catch (_) { /* See above. */ }
  return value;
}

export function clearOperation(slot) {
  memory.delete(slot);
  try { window.sessionStorage.removeItem(slot); } catch (_) { /* See above. */ }
}

export function newOperationKey() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function beginOperation(scope, intent) {
  const slot = operationSlot(scope, intent);
  return { slot, ...readOperation(slot) || saveOperation(slot, { key: newOperationKey() }) };
}

export function isDefinitive(response) {
  if (response.error === 'parse_error') return false;
  if (['idempotency_expired', 'idempotency_conflict', 'authorization_changed'].includes(response.error)) return false;
  return response.status >= 200 && response.status < 500 && response.status !== 429;
}

export function finishOperation(operation, response) {
  if (operation && isDefinitive(response)) clearOperation(operation.slot);
  return response;
}
